<template>
    <div class="button-message-editor" :key="renderId">
        <div class="platform-compatibility-warning" :class="{ 'warning-danger': hasUnsupportedChannels, 'warning-info': !hasUnsupportedChannels }" v-if="hasUnsupportedChannels || !connectedChannels.length">
            <span class="material-icons warning-icon">{{ hasUnsupportedChannels ? 'warning_amber' : 'info_outline' }}</span>
            <span class="warning-text">{{ hasUnsupportedChannels ? $t('chatbotv2.platformCompatWarning') : $t('chatbotv2.platformCompatNoChannel') }}</span>
            <a class="warning-detail-link" @click="showCompatibilityModal = true">{{ $t('chatbotv2.platformCompatDetail') }}</a>
        </div>
        <div class="text-editor-wrapper">
            <div class="text-editor">
                <TiptapEditor
                    :placeholder="$t('chatbotv2.enterYourMessage')"
                    :show-attributes="showAttributes === undefined || showAttributes === true"
                    :show-emoji="showEmoji === undefined || showEmoji === true"
                    v-model="clone.message"
                    :max-character-count="640"
                    :whatsapp-mode="whatsappMode"
                    :allowed-attributes="allowedAttributes"
                    :preview-mode="previewMode"
                />
            </div>
        </div>
        
        <div class="buttons-wrapper">
            <div class="button-item" v-for="(button, i) in clone.buttons" :key="button.id">
                <a-popover
                    :visible="editingButtonPopupVisibleMap[button.id]"
                    placement="left"
                    @visibleChange="(e) => visibleChange(button.id, e)"
                    trigger="click"
                    :key="button.id"
                    :overlayClassName="'no-padding'"
                >
                    <div slot="content">
                        <ActionButtonUpdater
                            v-model="clone.buttons[i]"
                            :on-completed="
                                () => {
                                    editingButtonPopupVisibleMap[button.id] = false;
                                }
                            "
                            :popup-visible="editingButtonPopupVisibleMap[button.id]"
                            :flattedDiagram="flattedDiagram"
                            :whatsapp-mode="whatsappMode"
                            :allowed-attributes="allowedAttributes"
                        />
                    </div>

                    <span
                        class="d-flex justify-content-center align-items-center w-100 h-100"
                        @click="openEditButtonPopup(button.id)"
                        >{{ button.title }}</span
                    >
                    <div class="button-controls" v-if="clone.buttons.length > 1">
                        <md-button class="md-icon-button md-mini delete" @click="removeButton(button.id)">
                            <md-icon>delete_outline</md-icon>
                        </md-button>
                        <md-button class="md-icon-button md-mini" @click.prevent="moveUpButton(button.id)">
                            <md-icon>arrow_upward</md-icon>
                        </md-button>
                        <md-button class="md-icon-button md-mini" @click.prevent="moveDownButton(button.id)">
                            <md-icon>arrow_downward</md-icon>
                        </md-button>
                    </div>
                </a-popover>
            </div>
            <div class="buttons-add" v-if="!previewMode">
                <div class="buttons-add-line"></div>
                <div class="action-buttons-row">
                    <div class="buttons-add-wrapper" @click="addButton">
                        <div class="buttons-add-plus">
                            <span class="material-icons"> add </span>
                        </div>
                        <div class="buttons-add-text">{{ $t("chatbotv2.addButton") }}</div>
                    </div>
                    
                    <!-- WhatsApp Settings Button -->
                    <div class="buttons-add-wrapper whatsapp-settings" v-if="clone.buttons.length > 3" @click="showWhatsappSettings = true">
                        <div class="buttons-add-plus whatsapp-plus">
                            <span class="material-icons">settings</span>
                        </div>
                        <div class="buttons-add-text">WhatsApp Ayarları</div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- WhatsApp Settings Modal -->
        <WhatsappListSettingsModal
            v-if="showWhatsappSettings"
            :value="clone.whatsappListSettings"
            @save="handleWhatsappSettingsSave"
            @cancel="showWhatsappSettings = false"
        />

        <!-- Platform Uyumluluk Modalı -->
        <Modal2
            v-if="showCompatibilityModal"
            design="v3"
            :show-title="false"
            :show-footer="false"
            @cancel="showCompatibilityModal = false"
            :large="true"
            className="platform-compat-modal"
        >
            <div class="compat-header">
                <span class="material-icons compat-header-icon">info</span>
                <div>
                    <div class="compat-title">{{ $t('chatbotv2.platformCompatTitle') }}</div>
                    <div class="compat-subtitle">{{ connectedChannels.length ? $t('chatbotv2.platformCompatSubtitleConnected') : $t('chatbotv2.platformCompatSubtitleAll') }}</div>
                </div>
            </div>
            <table class="compat-table">
                <thead>
                    <tr>
                        <th class="compat-th-platform">Platform</th>
                        <th v-for="feature in featureList" :key="feature.key">{{ feature.label }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in connectedChannelsSupport" :key="item.channelId">
                        <td class="compat-platform-cell">
                            <span class="compat-channel-name">{{ item.channelName }}</span>
                            <span class="compat-channel-type">{{ item.channelType }}</span>
                        </td>
                        <td v-for="feature in featureList" :key="feature.key" class="compat-feature-cell">
                            <span
                                class="material-icons"
                                :class="item.support[feature.key] ? 'compat-yes' : 'compat-no'"
                            >{{ item.support[feature.key] ? 'check_circle' : 'cancel' }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="compat-footer-note">
                <span class="material-icons">info_outline</span>
                <span>{{ $t('chatbotv2.platformCompatNote') }}</span>
            </div>
        </Modal2>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import TiptapEditor from "../../common/TiptapEditor";
import { generateRandomId } from "../../../../../../../utils/session";
import ActionButtonUpdater from "../../common/ActionButtonUpdater";
import WhatsappListSettingsModal from "./WhatsappListSettingsModal.vue";
import Modal2 from "../../../../../../../components/common/Modal2.vue";
import _ from "lodash";

const PLATFORM_BUTTON_SUPPORT = {
    'webchat': { name: 'Web', BUTTON: true, QUICK_REPLY: true, GALLERY: true, OPEN_URL: true, PHONE_CALL: true, OPEN_MOMENT: true },
    'instagram': { name: 'Instagram', BUTTON: true, QUICK_REPLY: true, GALLERY: true, OPEN_URL: true, PHONE_CALL: true, OPEN_MOMENT: true },
    'instagram-v2': { name: 'Instagram', BUTTON: true, QUICK_REPLY: true, GALLERY: true, OPEN_URL: true, PHONE_CALL: true, OPEN_MOMENT: true },
    'messenger': { name: 'Messenger', BUTTON: true, QUICK_REPLY: true, GALLERY: true, OPEN_URL: true, PHONE_CALL: true, OPEN_MOMENT: true },
    'telegram': { name: 'Telegram', BUTTON: true, QUICK_REPLY: true, GALLERY: true, OPEN_URL: true, PHONE_CALL: true, OPEN_MOMENT: false },
    'whatsapp-business-api': { name: 'WhatsApp Business', BUTTON: true, QUICK_REPLY: true, GALLERY: true, OPEN_URL: false, PHONE_CALL: false, OPEN_MOMENT: false },
    'whatsapp-business-api-supsis': { name: 'WhatsApp Business (WABA)', BUTTON: true, QUICK_REPLY: true, GALLERY: true, OPEN_URL: false, PHONE_CALL: false, OPEN_MOMENT: false },
    'whatsapp-unipile': { name: 'WhatsApp (Unipile)', BUTTON: false, QUICK_REPLY: false, GALLERY: false, OPEN_URL: false, PHONE_CALL: false, OPEN_MOMENT: false },
    'linkedin-unipile': { name: 'LinkedIn (Unipile)', BUTTON: false, QUICK_REPLY: false, GALLERY: false, OPEN_URL: false, PHONE_CALL: false, OPEN_MOMENT: false },
    'telegram-unipile': { name: 'Telegram (Unipile)', BUTTON: false, QUICK_REPLY: false, GALLERY: false, OPEN_URL: false, PHONE_CALL: false, OPEN_MOMENT: false },
    'trendyol': { name: 'Trendyol', BUTTON: false, QUICK_REPLY: false, GALLERY: false, OPEN_URL: false, PHONE_CALL: false, OPEN_MOMENT: false },
};

const FEATURE_KEYS = ['BUTTON', 'QUICK_REPLY', 'GALLERY', 'OPEN_URL', 'PHONE_CALL', 'OPEN_MOMENT'];

const log = log2("ButtonMessageEditor");
export default {
    name: "ButtonMessageEditor",
    components: { TiptapEditor, ActionButtonUpdater, WhatsappListSettingsModal, Modal2 },
    props: {
        value: {
            type: Object,
            required: true,
            default: {
                type: "BUTTON",
                delay: 2,
                message: "",
                buttons: [
                    {
                        id: "",
                        title: "Button",
                        type: "SEND_MESSAGE",
                        payload: {},
                        actions: [], //{type: "SET_ATTRIBUTE", payload: {attribute:"custom",value:1} },
                    },
                ],
            },
        },
        flattedDiagram: {
            type: Array,
            default: () => [],
        },
        allowedAttributes: {
            type: Array,
            default: () => [],
        },
        whatsappMode: {
            type: Boolean,
            default: false,
        },
        previewMode: {
            type: Boolean,
            default: false,
        },
        showEmoji: {
            type: Boolean,
            default: true,
        },
        showAttributes: {
            type: Boolean,
            default: true,
        },
        showEmojiPicker: {
            type: Boolean,
            default: true,
        },
        storyId: {
            type: String,
            default: "",
        },
    },
    data() {
        const clonedValue = _.cloneDeep(this.value);
        // Ensure whatsappListSettings exists for backward compatibility
        if (!clonedValue.whatsappListSettings) {
            clonedValue.whatsappListSettings = {
                globalButtonTitle: "",
                sectionTitle: "",
                sectionSubTitle: "",
                footer: "",
            };
        }
        return {
            editingButtonPopupVisibleMap: {},
            renderId: 0,
            clone: clonedValue,
            showWhatsappSettings: false,
            showCompatibilityModal: false,
        };
    },
    computed: {
        featureList() {
            const labelMap = {
                'BUTTON': 'platformCompatButton',
                'QUICK_REPLY': 'platformCompatQuickReply',
                'GALLERY': 'platformCompatGallery',
                'OPEN_URL': 'platformCompatOpenUrl',
                'PHONE_CALL': 'platformCompatPhoneCall',
                'OPEN_MOMENT': 'platformCompatOpenMoment',
            };
            return FEATURE_KEYS.map((key) => ({ key, label: this.$t('chatbotv2.' + labelMap[key]) }));
        },
        connectedChannels() {
            const storyId = this.storyId;
            if (!storyId) return [];
            const channels = this.$store.state.channel.channels || [];
            return channels.filter((ch) => {
                const forwardStory = _.get(ch, 'forward.story');
                const commentForwardStory = _.get(ch, 'commentForward.story');
                if (!forwardStory && !commentForwardStory) return false;
                return String(forwardStory) === String(storyId) || String(commentForwardStory) === String(storyId);
            });
        },
        connectedChannelsSupport() {
            const defaultSupport = { name: 'Bilinmeyen', BUTTON: false, QUICK_REPLY: false, GALLERY: false, OPEN_URL: false, PHONE_CALL: false, OPEN_MOMENT: false };
            if (this.connectedChannels.length) {
                return this.connectedChannels.map((ch) => {
                    const support = PLATFORM_BUTTON_SUPPORT[ch.type] || defaultSupport;
                    return { channelId: ch._id, channelName: ch.name || support.name, channelType: ch.type, support };
                });
            }
            return Object.keys(PLATFORM_BUTTON_SUPPORT).map((type) => {
                const support = PLATFORM_BUTTON_SUPPORT[type];
                return { channelId: type, channelName: support.name, channelType: type, support };
            });
        },
        hasUnsupportedChannels() {
            if (!this.connectedChannels.length) return false;
            return this.connectedChannels.some((ch) => {
                const s = PLATFORM_BUTTON_SUPPORT[ch.type];
                if (!s) return true;
                return !s.BUTTON || !s.QUICK_REPLY || !s.GALLERY || !s.OPEN_URL || !s.PHONE_CALL || !s.OPEN_MOMENT;
            });
        },
    },
    methods: {
        openEditButtonPopup(buttonId) {
            Object.keys(this.editingButtonPopupVisibleMap).forEach((key) => {
                this.editingButtonPopupVisibleMap[key] = false;
            });
            this.editingButtonPopupVisibleMap[buttonId] = true;
        },
        visibleChange(buttonId, visible) {
            const attributeFormIsEnabled = !!document.getElementById("add-custom-attr-modal");
            log("visibleChange", { buttonId, visible, attributeFormIsEnabled });
            if (!visible && attributeFormIsEnabled) {
                return;
            }
            Object.keys(this.editingButtonPopupVisibleMap).forEach((key) => {
                this.editingButtonPopupVisibleMap[key] = false;
            });
            this.editingButtonPopupVisibleMap[buttonId] = visible;
            this.renderId++;
        },
        addButton() {
            const id = generateRandomId();
            this.openEditButtonPopup(id);
            this.clone = {
                ...this.clone,
                buttons: [
                    ...this.clone.buttons,
                    {
                        id,
                        title: `Button ${this.clone.buttons.length + 1}`,
                        type: "SEND_MESSAGE",
                        payload: {},
                        actions: [], //{type: "SET_ATTRIBUTE", payload: {attribute:"custom",value:1} },
                    },
                ],
            };
        },
        removeButton(id) {
            this.clone = {
                ...this.clone,
                buttons: this.clone.buttons.filter((button) => button.id !== id),
            };
        },
        moveUpButton(id) {
            const index = this.clone.buttons.findIndex((button) => button.id === id);
            if (index > 0) {
                const temp = this.clone.buttons[index - 1];
                this.clone.buttons[index - 1] = this.clone.buttons[index];
                this.clone.buttons[index] = temp;
            }
            this.clone = _.cloneDeep(this.clone);
        },
        moveDownButton(id) {
            const index = this.clone.buttons.findIndex((button) => button.id === id);
            if (index < this.clone.buttons.length - 1) {
                const temp = this.clone.buttons[index + 1];
                this.clone.buttons[index + 1] = this.clone.buttons[index];
                this.clone.buttons[index] = temp;
            }
            this.clone = _.cloneDeep(this.clone);
        },
        handleWhatsappSettingsSave(settings) {
            this.clone.whatsappListSettings = settings;
            this.showWhatsappSettings = false;
        },
        emitValue() {
            if (!_.isEqual(this.clone, this.value)) {
                log("emitValue", this.clone);
                this.$emit("input", this.clone);
            }
        },
    },
    mounted() {
        log("mounted");
    },
    watch: {
        value: {
            handler(val) {
                log("watch value-->", val);
                const clonedVal = _.cloneDeep(val);
                // Ensure whatsappListSettings exists for backward compatibility
                if (!clonedVal.whatsappListSettings) {
                    clonedVal.whatsappListSettings = {
                        globalButtonTitle: "",
                        sectionTitle: "",
                        sectionSubTitle: "",
                        footer: "",
                    };
                }
                this.clone = clonedVal;
            },
            deep: true,
        },
        clone: {
            handler(val) {
                log("watch clone-->", val);
                this.emitValue();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.ProseMirror {
    color: #22272d;
    font-size: 16px;
    -webkit-font-smoothing: antialiased !important;
    -moz-osx-font-smoothing: grayscale;
    font-family: Source Sans Pro, sans-serif;
    font-weight: 400;
}

.platform-compatibility-warning {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 6px 6px 0 0;
    font-size: 11px;
    line-height: 1.4;

    .warning-icon {
        font-size: 16px;
        flex-shrink: 0;
    }

    .warning-text {
        flex: 1;
    }

    .warning-detail-link {
        color: #006cff;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
        font-size: 11px;

        &:hover {
            text-decoration: underline;
        }
    }

    &.warning-danger {
        background: #fff8e1;
        border: 1px solid #ffe082;

        .warning-icon { color: #f59e0b; }
        .warning-text { color: #92400e; }
    }

    &.warning-info {
        background: #e6f7ff;
        border: 1px solid #91d5ff;

        .warning-icon { color: #1890ff; }
        .warning-text { color: #096dd9; }
    }
}

.button-message-editor {
    width: 240px;
    box-sizing: border-box;
    margin: 0;
    outline: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    position: relative;

    .text-editor-wrapper {
        position: relative;
        user-select: auto;
        width: 100%;

        .text-editor {
            border-radius: 8px 8px 0px 0px;
            border: 1px solid #fff;
            overflow-x: hidden;
            overflow-y: auto;
            transition: border 0.3s;
            height: 150px;
            background: white;

            &:hover {
                border: 1px solid #006cff !important;
            }

            .editable-editor {
                border-radius: 8px 8px 0px 0px;
                overflow-x: hidden;
                overflow-y: auto;

                .ProseMirror {
                    height: 100%;
                }

                &.focused {
                    border: none !important;
                }

                &:focus {
                    border: none !important;
                }

                &:hover {
                    border: none !important;
                    resize: none;
                }
            }

            .editable-editor-wrapper {
                color: #22272d;
                font-size: 16px;
                width: 100%;
                height: calc(100% - 10px);

                .ProseMirror {
                    width: 100%;
                    height: 100%;
                    min-height: 115px;
                    max-height: 165px;
                    padding: 20px 20px 0;
                }
            }
        }
    }

    .buttons-wrapper {
        width: 100%;

        .button-item {
            align-items: center;
            cursor: pointer;
            display: flex;
            height: 42px;
            justify-content: center;
            padding: 0 10px;
            transition: all 0.3s;
            width: 100%;
            background: #fff;
            border-top: 1px solid #e6e6e6;
            position: relative;

            span {
                color: #006cff;
                font-size: 15px;
                font-weight: 600;
                line-height: 19px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                text-align: center;

                &:hover {
                    .button-controls {
                        opacity: 1;
                    }
                }
            }

            &:hover {
                .button-controls {
                    opacity: 1;
                }
            }

            .button-controls {
                align-items: center;
                display: flex;
                opacity: 0;
                position: absolute;
                right: -13px;
                top: -13px;
                transition: opacity 0.3s;
                z-index: 12;

                .md-button {
                    background-color: #fff;
                    align-items: center;
                    border-radius: 100%;
                    box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                    cursor: pointer;
                    display: flex;
                    justify-content: center;
                    margin-bottom: 7px;
                    transition: background-color 0.3s;
                    height: 20px;
                    width: 20px;
                    min-height: 20px !important;
                    min-width: 20px !important;

                    .md-icon {
                        color: #006cff;
                        font-size: 16px !important;
                    }

                    &.delete {
                        .md-icon {
                            color: #ff0000;
                        }
                    }
                }
            }
        }

        .buttons-add {
            align-items: center;
            display: flex;
            flex-direction: column;
            margin-bottom: 5px;
            width: 100%;

            .buttons-add-line {
                border-left: 1px dashed #b4c0c7;
                border-right: 1px dashed #b4c0c7;
                height: 28px;
                width: 0;
            }

            .action-buttons-row {
                display: flex;
                gap: 20px;
                align-items: center;
                justify-content: center;
                margin-left: 115px;
            }

            .buttons-add-wrapper {
                cursor: pointer;
                position: relative;
                -webkit-transition: all 0.3s;
                transition: all 0.3s;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;

                .buttons-add-text {
                    color: #475e7a;
                    font-size: 11px;
                    font-weight: 600;
                    line-height: 18px;
                    white-space: nowrap;
                    margin-top: 3px;
                }

                .buttons-add-plus {
                    align-items: center;
                    background: #fff;
                    border-radius: 100%;
                    box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                    display: flex;
                    height: 28px;
                    justify-content: center;
                    transition: all 0.3s;
                    width: 28px;

                    .material-icons {
                        font-size: 18px;
                    }

                    &:hover {
                        color: #fff;
                        background: #006cff;
                    }
                    
                    &.whatsapp-plus {
                        background: #25D366;
                        color: white;
                        
                        &:hover {
                            background: #128C7E;
                            transform: scale(1.1);
                        }
                    }
                }
                
                &.whatsapp-settings {
                    .buttons-add-text {
                        color: #25D366;
                    }
                }
            }
        }
    }
}
</style>

<style lang="scss">
.platform-compat-modal {
    .custom-modal-body {
        padding: 0 !important;
        gap: 0 !important;
    }

    .compat-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 20px 24px 16px;
        border-bottom: 1px solid #f0f0f0;

        .compat-header-icon {
            font-size: 28px;
            color: #1890ff;
            flex-shrink: 0;
        }

        .compat-title {
            font-size: 16px;
            font-weight: 700;
            color: #1a1a1a;
            line-height: 1.3;
        }

        .compat-subtitle {
            font-size: 12px;
            color: #8c8c8c;
            margin-top: 2px;
        }
    }

    .compat-table {
        width: 100%;
        border-collapse: collapse;

        th, td {
            padding: 10px 16px;
            text-align: center;
            border-bottom: 1px solid #f0f0f0;
            font-size: 13px;
        }

        th {
            background: #fafafa;
            font-weight: 600;
            color: #595959;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            white-space: nowrap;
        }

        .compat-th-platform {
            text-align: left;
        }

        .compat-platform-cell {
            text-align: left;
            white-space: nowrap;

            .compat-channel-name {
                font-weight: 600;
                color: #262626;
                display: block;
                line-height: 1.3;
            }

            .compat-channel-type {
                font-size: 11px;
                color: #bfbfbf;
            }
        }

        .compat-feature-cell {
            .compat-yes {
                color: #52c41a;
                font-size: 20px;
            }

            .compat-no {
                color: #ff4d4f;
                font-size: 20px;
            }
        }

        tbody tr {
            transition: background 0.15s;

            &:hover {
                background: #fafafa;
            }

            &:last-child td {
                border-bottom: none;
            }
        }
    }

    .compat-footer-note {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 14px 24px;
        border-top: 1px solid #f0f0f0;
        background: #fffbe6;
        font-size: 12px;
        color: #8c6d1f;
        line-height: 1.5;

        .material-icons {
            font-size: 16px;
            color: #faad14;
            flex-shrink: 0;
            margin-top: 1px;
        }
    }
}
</style>
